import {
  Button,
  type ColumnDef,
  ConfirmDialog,
  DataTable,
  EmptyState,
  InlineMessage,
  Modal,
  ModalContent,
  Spinner,
  Stack,
  StatusLabel,
  Text,
} from '@olytic/foundation';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { formatDate, formatMoney } from '../objects/format';
import {
  changedLines,
  type DraftRow,
  draftTotals,
  type EditorLine,
  newRow,
  problems,
  gapOf,
  type ScheduleEditorPayload,
  sortDraft,
  toDraft,
  toWire,
} from './scheduleEditor';

const money = (n: number) => formatMoney(n);

async function send(url: string, method: string, body?: unknown): Promise<ScheduleEditorPayload> {
  const res = await authenticatedFetch(url, {
    method,
    ...(body === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });
  const data = (await res.json().catch(() => null)) as (ScheduleEditorPayload & { error?: string }) | null;
  if (!res.ok) throw new Error(data?.error ?? `Could not save the schedules (${res.status}).`);
  return data as ScheduleEditorPayload;
}

/**
 * An opportunity's revenue schedules as an editable grid: its primary quote's
 * rows, a row per line and month. Cells edit in place, rows are added and
 * deleted, and Save sends the whole batch. A line whose rows change is marked
 * edited by hand and keeps its schedule when the quote regenerates, until it
 * is reverted here.
 */
export function RevenueScheduleEditor({
  opportunityId, onClose, onSaved,
}: { opportunityId: string; onClose: () => void; onSaved: () => void }): JSX.Element {
  const url = `/api/opportunities/${encodeURIComponent(opportunityId)}/revenue-schedules`;
  const [data, setData] = useState<ScheduleEditorPayload | null>(null);
  const [draft, setDraft] = useState<DraftRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState<EditorLine | null>(null);
  const [added, setAdded] = useState(0);

  const take = useCallback((payload: ScheduleEditorPayload) => {
    setData(payload);
    setDraft(sortDraft(toDraft(payload.rows), payload.lines));
  }, []);

  const reload = useCallback(() => {
    setError(null);
    send(url, 'GET').then(take, (e: unknown) => setError(e instanceof Error ? e.message : 'Could not load the schedules.'));
  }, [url, take]);
  useEffect(reload, [reload]);

  const lines = useMemo(() => data?.lines ?? [], [data]);
  const changed = useMemo(() => (data ? changedLines(lines, data.rows, draft) : new Set<string>()), [data, lines, draft]);
  const issues = useMemo(() => (data ? problems(lines, data.rows, draft, money) : []), [data, lines, draft]);
  const totals = useMemo(() => draftTotals(draft), [draft]);
  const nameOf = useMemo(() => new Map(lines.map((l) => [l.id, l.name])), [lines]);
  const dirty = changed.size > 0 || draft.length !== (data?.rows.length ?? 0);

  const save = async () => {
    if (!data?.quote) return;
    setSaving(true);
    setError(null);
    try {
      take(await send(url, 'PUT', { quote_id: data.quote.id, version: data.version, rows: toWire(draft) }));
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the schedules.');
    } finally {
      setSaving(false);
    }
  };

  const revert = async (line: EditorLine) => {
    if (!data?.quote) return;
    try {
      take(await send(`${url}/revert`, 'POST', { quote_id: data.quote.id, line_ids: [line.id] }));
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not revert the line.');
    } finally {
      setReverting(null);
    }
  };

  const lineColumns: ColumnDef<EditorLine>[] = [
    { key: 'name', label: 'Line', get: (l) => l.name },
    { key: 'quote_total', label: 'On the Quote', align: 'right', nowrap: true, render: (l) => (l.quote_total === null ? '—' : money(l.quote_total)) },
    { key: 'schedule', label: 'In the Schedule', align: 'right', nowrap: true, render: (l) => money(totals[l.id] ?? 0) },
    {
      key: 'difference',
      label: 'Difference',
      align: 'right',
      nowrap: true,
      render: (l) => {
        const gap = l.quote_total === null ? 0 : gapOf(totals[l.id] ?? 0, l.quote_total);
        return gap === 0 ? '—' : <StatusLabel tone="error">{gap < 0 ? '−' : '+'}{money(Math.abs(gap))}</StatusLabel>;
      },
    },
    {
      key: 'source',
      label: 'Schedule',
      nowrap: true,
      render: (l) =>
        changed.has(l.id) ? (
          <StatusLabel tone="warning">Unsaved changes</StatusLabel>
        ) : l.edited_at ? (
          <StatusLabel tone="accent">Edited by hand, {formatDate(l.edited_at)}{l.edited_by ? ` by ${l.edited_by}` : ''}</StatusLabel>
        ) : (
          <StatusLabel tone="neutral">From the quote</StatusLabel>
        ),
    },
  ];

  const set = (key: string, field: keyof DraftRow, value: string | null) =>
    setDraft((rows) => rows.map((r) => (r.key === key ? { ...r, [field]: value ?? '' } : r)));

  const rowColumns: ColumnDef<DraftRow>[] = [
    {
      key: 'quote_line_id',
      label: 'Line',
      width: 240,
      get: (r) => nameOf.get(r.quote_line_id) ?? '—',
      editor: { kind: 'select', options: lines.map((l) => ({ value: l.id, label: l.name })), getValue: (r) => r.quote_line_id },
      editActivation: 'click',
    },
    { key: 'period_start', label: 'Start', width: 150, nowrap: true, get: (r) => formatDate(r.period_start), editor: { kind: 'date', getValue: (r) => r.period_start }, editActivation: 'click' },
    { key: 'period_end', label: 'End', width: 150, nowrap: true, get: (r) => formatDate(r.period_end), editor: { kind: 'date', getValue: (r) => r.period_end }, editActivation: 'click' },
    {
      key: 'amount',
      label: 'Amount',
      width: 150,
      align: 'right',
      nowrap: true,
      get: (r) => (r.amount.trim() === '' || Number.isNaN(Number(r.amount)) ? r.amount : money(Number(r.amount))),
      editor: { kind: 'number', step: 0.01, getValue: (r) => r.amount },
      editActivation: 'click',
    },
    {
      key: 'hours',
      label: 'Hours',
      width: 100,
      align: 'right',
      nowrap: true,
      get: (r) => r.hours,
      editor: { kind: 'number', min: 0, step: 0.5, nullable: true, getValue: (r) => r.hours },
      editActivation: 'click',
    },
  ];

  const addRow = () => {
    const lineId = draft.at(-1)?.quote_line_id ?? lines[0]?.id;
    if (!lineId) return;
    setDraft((rows) => [...rows, newRow(rows, lineId, `new-${added}`)]);
    setAdded((n) => n + 1);
  };

  const quote = data?.quote;
  return (
    <Modal open onOpenChange={(open) => (open ? undefined : onClose())}>
      <ModalContent
        size="xl"
        title="Edit revenue schedules"
        description={quote ? `${quote.quote_number ?? 'The primary quote'}${quote.name ? ` · ${quote.name}` : ''}, the primary quote` : undefined}
        footer={
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="brand" onClick={() => void save()} disabled={!quote || !dirty || issues.length > 0} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        {!data && !error ? <Spinner label="Loading the schedules…" /> : null}
        {data && !quote ? (
          <EmptyState title="No primary quote" description="Make one of this opportunity's quotes primary, and its schedules can be edited here." />
        ) : null}
        {data && quote ? (
          <Stack>
            <Text tone="muted">
              A line you change keeps its schedule when the quote regenerates, until you revert it to the quote. Hours come from the
              line's products; hours you type are kept, and clearing them takes them from the products again.
            </Text>
            <DataTable<EditorLine>
              columns={lineColumns}
              rows={lines}
              getRowId={(l) => l.id}
              density="compact"
              rowActions={[{ label: 'Revert to the quote', onSelect: (l) => setReverting(l), hidden: (l) => !l.edited_at || dirty }]}
            />
            {issues.length ? (
              <InlineMessage tone="warning" role="status">
                {issues.slice(0, 4).join(' ')}
                {issues.length > 4 ? ` And ${issues.length - 4} more.` : ''}
              </InlineMessage>
            ) : null}
            <DataTable<DraftRow>
              columns={rowColumns}
              rows={draft}
              getRowId={(r) => r.key}
              density="compact"
              responsive={false}
              showRowNumbers
              onCellSave={(row, key, value) => set(row.key, key as keyof DraftRow, value)}
              rowActions={[{ label: 'Delete row', destructive: true, onSelect: (r) => setDraft((rows) => rows.filter((x) => x.key !== r.key)) }]}
              empty={<EmptyState title="No rows" description="Add a row, or generate the schedule on the quote." />}
              footerRow={
                <Button variant="subtle" size="sm" onClick={addRow} disabled={!lines.length}>
                  Add a row
                </Button>
              }
            />
          </Stack>
        ) : null}
        {error ? (
          <InlineMessage tone="error" role="alert" className="mt-3">
            {error}{' '}
            <Button variant="subtle" size="sm" onClick={reload}>Reload</Button>
          </InlineMessage>
        ) : null}
        <ConfirmDialog
          open={reverting !== null}
          onOpenChange={(open) => { if (!open) setReverting(null); }}
          title="Revert to the quote?"
          description={reverting ? `${reverting.name} goes back to the schedule its quote makes, and your edits to it are lost.` : ''}
          confirmLabel="Revert"
          onConfirm={() => (reverting ? revert(reverting) : undefined)}
        />
      </ModalContent>
    </Modal>
  );
}
